import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { IAddRegisteredDevice } from '@shared/interfaces/device/add-registered-device';
import { IRegisteredDevice } from '@shared/interfaces/device/registered-device';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { UserService } from '@shared/modules/account/services/user.service';
import { WindowService } from '@shared/services/window.service';
import { GroupManageComponent } from '../../components/group-manage/group-manage.component';
import { Subscription } from 'rxjs';
import { AccessService } from '@access/access.service';
import { PERMISSIONS } from '@access/permissions';

@Component({
  selector: 'app-device-add-modal',
  templateUrl: './device-add-modal.component.html',
  styleUrls: ['./device-add-modal.component.scss']
})
export class DeviceAddModalComponent implements OnInit {
  @ViewChild(GroupManageComponent) groupManageComponent!: GroupManageComponent;
  currentStep: number = 0;
  deviceNameForm!: UntypedFormGroup;
  loading = true;
  groupCreationModeEnabled: boolean = false;
  registeredDevices?: IRegisteredDevice[];
  selectedDevice!: IRegisteredDevice;

  private subscription = new Subscription();

  constructor(
    protected dialogRef: NbDialogRef<DeviceAddModalComponent>,
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private alertService: AlertService,
    private userService: UserService,
    private windowService: WindowService,
    private readonly accessService: AccessService
  ) { }

  ngOnInit() {
    this.createDeviceNameForm();
    this.getRegisteredDevices();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getRegisteredDevices() {
    this.subscription.add(
      this.api.getRegisteredDevices()
      .subscribe({
        error: err => this.loading = false,
        next: res => {
          this.registeredDevices = res;
          this.loading = false;
        }
      })
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  next(): void {
    this.currentStep += 1;
  }

  back(): void {
    this.currentStep -= 1;
  }

  createDeviceNameForm(): void {
    this.deviceNameForm = this.fb.group({
      deviceName: ["", Validators.compose([Validators.required, Validators.maxLength(30), Validators.minLength(2)])],
      deviceDescription: ["", Validators.compose([Validators.minLength(0), Validators.maxLength(200)])]
    });
  }

  hasError(name: string): boolean {
    const e = this.getFormControl(name);
    return !e.valid;
  }

  getFormControl = (name: string): UntypedFormControl => {
    return this.deviceNameForm.get(name) as UntypedFormControl;
  }

  connect(device: IRegisteredDevice) {
    this.updateDeviceNameForm(device.modelName);
    this.next();
    this.selectedDevice = device;
  }

  updateDeviceNameForm(name: string): void {
    this.deviceNameForm.setValue({
      deviceName: name || "",
      deviceDescription: ""
    });
  }

  onGroupCreationModeChange(flag: boolean): void {
    this.groupCreationModeEnabled = flag;
  }

  save(): void {
    if (this.groupManageComponent && this.groupManageComponent.showInputGroup) {
      this.groupManageComponent.createGroup().then(() => {
        this.updateDevice();
      });
    } else {
      this.updateDevice();
    }
  }

  updateDevice() {
    this.loading = true;

    const data: IAddRegisteredDevice = {
      id: this.selectedDevice.id,
      deviceName: this.deviceNameForm.value.deviceName,
      typeId: this.selectedDevice.typeId,
      serialNumber: this.selectedDevice.serialNumber,
      groupId: this.groupManageComponent && this.groupManageComponent.selectedGroup ? this.groupManageComponent.selectedGroup : undefined,
      clientId: this.selectedDevice.clientId,
      deviceDescription: this.deviceNameForm.value.deviceDescription
    };
    
    this.api.postAddRegisteredDevice(data)
      .subscribe({
        next: res => {
          this.loading = false;
          this.alertService.success( "device.add-wizard.device-register-success", 5000);
          this.dialogRef.close();
        },
        error: error => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  checkDisabled(): boolean {
    if (this.groupManageComponent && this.groupManageComponent.showInputGroup) {
      return this.groupManageComponent.newGroupForm.invalid || this.deviceNameForm.invalid;
    }

    return this.deviceNameForm.invalid;
  }

  groupManagementIsAvailable(): boolean {
    return this.accessService.hasAll([PERMISSIONS.DeviceGroups.View, PERMISSIONS.DeviceGroups.Manage])
  }

  openSupportTab() {
    const userLanguage = this.userService.user?.language ?? "en";

    switch (userLanguage) {
      case "en":
        this.windowService.blankOpen("https://support.roboze.com/wp-content/uploads/2020/12/incloud_user_guide_21122020.pdf");
        break;
      case "pl":
        this.windowService.blankOpen("https://support.roboze.com/wp-content/uploads/2020/12/incloud_przewodnik_uzytkownika_21122020.pdf");
        break;
      case "zh":
        this.windowService.blankOpen("https://support.roboze.com/wp-content/uploads/2020/12/incloud_user_guide_21122020.pdf");
        break;
      default:
        this.windowService.blankOpen("https://support.roboze.com/wp-content/uploads/2020/12/incloud_user_guide_21122020.pdf");
    }
  }
}
